import React, { useState } from "react";
import {
  Box,
  Grid,
  TextareaAutosize,
  Typography,
  autocompleteClasses,
  outlinedInputClasses,
  FormControlLabel
} from "@mui/material";
import { buttonClasses } from "@mui/material/Button";
import MzInput from "@markaaz/ui/MzInputControls";
import MzCheckbox from "@markaaz/ui/MzCheckbox";
// import { useFormik } from "formik";
import I18n from "i18next";
import MzButton from "@markaaz/ui/MzButton";
import { useFormik } from "formik";
import * as yup from "yup";
import MzAutocomplete from "@markaaz/ui/MzAutocomplete";
import { MatchConfidanceScore } from "./MatchConfidanceScore";

const sx = {
  wrap: {
    display: "flex",
    padding: "40px",
    gap: "40px",
    backgroundColor: "common.white",
    width: "660px",
    borderBottom: (theme) => `1px solid ${theme.palette.blue.c20}`,
    boxSizing: "content-box"
  },
  button: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "40px 5px",
    gap: "20px"
  },
  textArea: {
    flexGrow: "1",
    mt: "24px",
    textarea: (theme) => {
      return {
        ...theme.typography.body2,
        width: "100%",
        display: "flex",
        height: "66px",
        padding: "13px 5px 5px 15px",
        alignItems: "flex-start",
        gap: "10px",
        alignSelf: "stretch",
        color: "darkBlue.c100",
        borderColor: "darkBlue.c20",
        borderRadius: "8px",
        maxWidth: "676px",
        "&:focus-visible": {
          borderColor: "blue.c100",
          outline: 0
        }
      };
    }
  },
  autoComplete: {
    [`.${autocompleteClasses.root} .${outlinedInputClasses.root}`]: {
      padding: "5px"
    }
  },
  nameMatchWrap: {
    border: "none",
    borderRadius: "0 0 8px 8px",
    display: "flex",
    alignItems: "center",
    justifyContents: "center",
    flexDirection: "column",
    padding: "40px",
    gap: "40px",
    backgroundColor: "common.white",
    width: "660px",
    boxSizing: "content-box"
  },
  advancedMatchWrap: {
    display: "flex",
    width: "660px",
    padding: "40px",
    gap: "24px",
    backgroundColor: "common.white",
    flexDirection: "column",
    color: (theme) => theme.palette.darkBlue.c100,
    borderRadius: "8px 8px 0px 0px",
    borderBottom: (theme) => `1px solid ${theme.palette.blue.c20}`,
    boxSizing: "content-box"
  },
  advancedMatchHeader: {
    display: "flex",
    width: "660px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "8px",
    alignSelf: "stretch"
  },
  advancedMatchConfig: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexShrink: 0
  },
  matchCount: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexShrink: 0
  },
  formControl: {
    marginLeft: "0px",
    "& .MuiFormControlLabel-root": {
      margin: 0
    }
  },
  resetButton: {
    [`&.${buttonClasses.outlined}.${buttonClasses.outlinedSizeLarge}`]: {
      width: "94px",
      height: "48px"
    }
  },
  submitButton: {
    [`&.${buttonClasses.contained}.${buttonClasses.containedSizeLarge}`]: {
      width: "108px",
      height: "48px"
    }
  },
  checkbox: (theme) => ({
    "& .MuiTypography-root": {
      ...theme.typography.body2M,
      paddingLeft: "10px"
    },
    "& .MuiCheckbox-root": {
      color: theme.palette.darkBlue.c50
    },
    "& .Mui-checked": {
      color: theme.palette.blue.c100
    },
    paddingLeft: "20px",
    "&.Mui-disabled": {
      "& .MuiCheckbox-root": {
        color: theme.palette.darkBlue.c20
      },
      "& .MuiFormControlLabel-label": {
        color: theme.palette.darkBlue.c30
      }
    }
  }),
  matchCountAutoComplete: {
    maxWidth: "88px",
    svg: {
      color: (theme) => theme.palette.darkBlue.c50
    }
  },
  countryAutoComplete: {
    svg: {
      color: (theme) => theme.palette.darkBlue.c50
    }
  }
};

const validationSchema = yup.object({
  legalName: yup.string().required("Company Legal Name is required"),
  address: yup.string().required("Street Address is required"),
  postalCode: yup.string().required("Postal Code / Zip Code is required"),
  country: yup.string().required("Country Code is required"),
  locality: yup.string().required("Locality is required")
});

export default function NewLookupForm({
  onSubmit,
  country,
  loading,
  onResetClick,
  showNameMatchingField,
  showAdvancedMatch,
  markaazConfidenceScore,
  onEditThreshold,
}) {
  const [isFormValid, setFormValid] = useState(false);
  function validateForm(values) {
    validationSchema
      .validate(values)
      .then(() => {
        setFormValid(true);
      })
      .catch(() => {
        setFormValid(false);
      });
  }

  const initialValues = {
    legalName: "",
    dba: "",
    address: "",
    locality: "",
    region: "",
    postalCode: "",
    country: null,
    phoneNo: "",
    website: "",
    taxId: "",
    nationalId: "",
    fullName: "",
    matchCount: 1,
    showGrades: false,
    showNonMatches: false
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: async () => {
      const { fullName, phoneNo, ...newFormikValues } = formik.values;
      const submitValues = {
        ...newFormikValues,
        phoneNo: formik.values.phoneNo && formik.values.phoneNo.replaceAll("-", ""),
        names: inputArray ? inputArray.map((item) => ({ fullName: item })) : []
      };
      await onSubmit(submitValues);
      setFormValid(false);
    },
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    validate: validateForm
  });

  function handleReset() {
    formik.resetForm({ values: initialValues });
    onResetClick();
  }

  const inputArray = formik?.values?.fullName !== "" && formik?.values?.fullName?.split(/\s*,\s*/);

  const number = formik.values.phoneNo?.replace(/\D/g, "");
  let formattedValue = "";
  if (number?.length > 0) {
    formattedValue = number.slice(0, 3);
    if (number.length > 3) {
      formattedValue += `-${number.slice(3, 6)}`;
      if (number.length > 6) {
        formattedValue += `-${number.slice(6, 10)}`;
      }
    }
  }

  const hasAnyValue = (values) => {
    for (const key in values) {
      if (values[key]) {
        return true;
      }
    }
    return false;
  };

  const matchCountOptions = Array.from({ length: 10 }, (_, index) => ({
    label: (index + 1).toString(),
    value: index + 1
  }));

  return (
    <Box>
      <MatchConfidanceScore
        markaazConfidenceScore={markaazConfidenceScore}
        onEditThreshold={onEditThreshold}
      />

      <Box sx={sx.advancedMatchWrap} marginTop="40px">
        <Box sx={sx.advancedMatchHeader}>
          <Typography variant="body1B">Advanced Match</Typography>
          <Typography variant="body2">
            The features and data groups returned are governed by your contract terms. Show grades provides an option to
            discover how the input data fields are graded for their match quality with the data existing in the Markaaz
            Directory. Please consult with your Markaaz Account Manager if clarification is required.
          </Typography>
        </Box>
        <Box sx={sx.advancedMatchConfig}>
          <Box sx={sx.matchCount}>
            <Typography variant="body2M" sx={{ color: showAdvancedMatch ? "darkBlue.c100" : "darkBlue.c30" }}>
              Match Count
            </Typography>
            <MzAutocomplete
              sx={sx.matchCountAutoComplete}
              value={formik.values.matchCount}
              onChange={(event, newValue) => {
                formik.setFieldValue("matchCount", newValue?.value);
              }}
              options={matchCountOptions}
              disabled={!showAdvancedMatch}
              disableClearable
            />
          </Box>
          <FormControlLabel
            sx={sx.formControl}
            control={
              <MzCheckbox
                sx={sx.checkbox}
                label="Show Grades"
                name="showGrades"
                checked={formik.values.showGrades}
                onChange={(event, newValue) => {
                  formik.setFieldValue("showGrades", newValue);
                }}
                disabled={!showAdvancedMatch}
              />
            }
          />
          <FormControlLabel
            sx={sx.formControl}
            control={
              <MzCheckbox
                sx={sx.checkbox}
                label="Show Non-Matches"
                name="showNonMatches"
                checked={formik.values.showNonMatches}
                onChange={(event, newValue) => {
                  formik.setFieldValue("showNonMatches", newValue);
                }}
                disabled={!showAdvancedMatch}
              />
            }
          />
        </Box>
      </Box>
      <Box sx={sx.wrap} borderRadius={showAdvancedMatch ? "0px" : "8px 8px 0px 0px"}>
        <Grid container spacing={6}>
          <Grid item xs={6}>
            <MzInput
              name="legalName"
              label="Company Legal Name"
              required
              fullWidth
              value={formik.values.legalName}
              error={formik.touched.legalName && !!formik.errors.legalName}
              helperText={formik.touched.legalName && formik.errors.legalName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="dba"
              label="DBA"
              fullWidth
              value={formik.values.dba}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="address"
              label="Street Address"
              required
              fullWidth
              value={formik.values.address}
              error={formik.touched.address && !!formik.errors.address}
              helperText={formik.touched.address && formik.errors.address}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="locality"
              label="Locality / City"
              fullWidth
              required
              value={formik.values.locality}
              error={formik.touched.locality && !!formik.errors.locality}
              helperText={formik.touched.locality && formik.errors.locality}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={4}>
            <MzInput
              name="region"
              label="Region / State"
              fullWidth
              value={formik.values.region}
              error={formik.touched.region && !!formik.errors.region}
              helperText={formik.touched.region && formik.errors.region}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={4}>
            <MzInput
              name="postalCode"
              label="Postal Code / Zip Code"
              required
              fullWidth
              value={formik.values.postalCode?.replace(/[^a-zA-Z0-9\s]/g, "")}
              error={formik.touched.postalCode && !!formik.errors.postalCode}
              helperText={formik.touched.postalCode && formik.errors.postalCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={4}>
            <Box sx={sx.autoComplete}>
              <MzAutocomplete
                sx={sx.countryAutoComplete}
                name="country"
                label="Country"
                required={true}
                fullWidth
                value={country.find((country) => country.label === formik.values.country) || null}
                isOptionEqualToValue={(option, value) => option.name === value}
                onChange={(event, newValue) => {
                  formik.setFieldValue("country", newValue?.label);
                }}
                options={country}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                  <li {...props} key={option.label}>
                    {option.name}
                  </li>
                )}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="phoneNo"
              label="Phone Number"
              fullWidth
              InputProps={{ inputProps: { maxLength: 12 } }}
              value={formattedValue}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="website"
              label="Website"
              fullWidth
              value={formik.values.website}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="taxId"
              label="Tax ID"
              fullWidth
              value={formik.values.taxId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={6}>
            <MzInput
              name="nationalId"
              label="National ID"
              fullWidth
              value={formik.values.nationalId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
        </Grid>
      </Box>
      {showNameMatchingField && (
        <Box sx={sx.nameMatchWrap}>
          <Grid container spacing={6} color="darkBlue.c100" padding="20px"  margin="0px">
            <Typography variant="body1B">Name Matching (optional)</Typography>
            <Typography variant="body2">
              Enter a list of employee names to validate their association with the business being queried. Separate all
              names with a comma and make sure to enter all required business details in the above section.
            </Typography>
            <Box sx={sx.textArea}>
              <TextareaAutosize
                minRows={4}
                name="fullName"
                onChange={formik.handleChange}
                value={formik.values.fullName}
                onBlur={formik.handleBlur}
                placeholder="Ex: John Doe, John Michael Smith, Bob Jones Jr"
              />
            </Box>
          </Grid>
        </Box>
      )}
      <Box sx={sx.button}>
        <MzButton
          sx={sx.resetButton}
          variant="outlined"
          color="secondary"
          size="large"
          onClick={handleReset}
          disabled={!hasAnyValue(formik.values)}
        >
          {I18n.t("matchConfidence.reset")}
        </MzButton>
        <MzButton
          sx={sx.submitButton}
          variant="contained"
          color="primary"
          size="large"
          onClick={formik.handleSubmit}
          disabled={!isFormValid}
          loading={loading}
        >
          {I18n.t("common.submit")}
        </MzButton>
      </Box>
    </Box>
  );
}
